import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import StatutoPage from "./pages/StatutoPage";
import RegolamentoPage from "./pages/RegolamentoPage";
import AdvisoryBoardPage from "./pages/AdvisoryBoardPage";
import ContattiPage from "./pages/ContattiPage";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import MembersPage from "./pages/MembersPage";
import ProfilePage from "./pages/ProfilePage";
import JobsPage from "./pages/JobsPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import NewsPage from "./pages/NewsPage";
import PaymentsPage from "./pages/PaymentsPage";
import AdminApplicationsPage from "./pages/AdminApplicationsPage";
import AdminVotesPage from "./pages/AdminVotesPage";
import GroupsManagementPage from "./pages/GroupsManagementPage";
import MemberNewsPage from "./pages/MemberNewsPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentIntentsPage from "./pages/PaymentIntentsPage";
import ConfigurationsPage from "./pages/ConfigurationsPage";
import ArchivePage from "./pages/ArchivePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import publicRoutes from "./config/publicRoutes";
import RoleManagementPage from "./pages/RoleManagementPage";
import StripePaymentsPage from "./pages/StripePaymentsPage";
import PublicActivityRegistrationPage from "./pages/PublicActivityRegistrationPage";
import ActivityRegistrationConfirmPage from "./pages/ActivityRegistrationConfirmPage";
import ActivityPostLoginRegistration from "./pages/ActivityPostLoginRegistration";
import ActivityPaymentPage from "./pages/ActivityPaymentPage";
import VotingResultsPage from "./pages/VotingResultsPage";
import VotePage from "./pages/VotePage";
import { evaluateProfileCompletion } from "./utils/profileCompletion";
import QuoteSituationPage from "./pages/QuoteSituationPage";
import AdminRecentAccessesPage from "./pages/AdminRecentAccessesPage";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = rawBackendUrl ? rawBackendUrl.replace(/\/$/, "") : "http://api.acbase.org";
const API = `${BACKEND_URL}/api`;


// Set axios defaults
axios.defaults.baseURL = API;

// Auth context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [missingProfileFields, setMissingProfileFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const identifiedUserRef = useRef(null);
  const shouldEnforceProfileCompletion = process.env.NODE_ENV === "production";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      identifiedUserRef.current = null;
      return;
    }

    if (identifiedUserRef.current === user.id) {
      return;
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
    if (window.posthog?.identify) {
      window.posthog.identify(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        name: fullName || undefined,
      });
      identifiedUserRef.current = user.id;
    }
  }, [user]);

  useEffect(() => {
    const profileCompletion = evaluateProfileCompletion(user);
    setIsProfileComplete(profileCompletion.isComplete);
    setMissingProfileFields(profileCompletion.missingFields);
  }, [user]);

  const fetchUser = async () => {
    try {
      console.log("Fetching user with token from localStorage");
      const response = await axios.get("/auth/me");
      console.log("User fetched successfully:", response.data);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      console.error("Error details:", error.response?.status, error.response?.data);
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    // Fetch complete user data including role
    await fetchUser();
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    if (window.posthog?.reset) {
      window.posthog.reset();
    }
    setUser(null);
  };

  const renderProtectedRoute = (component, options = {}) => {
    const { allowIncompleteProfile = false, authorize } = options;
    const requiresProfileCompletion = Boolean(user?.requires_profile_completion);

    if (!user) {
      return <Navigate to="/login" />;
    }

    if (
      shouldEnforceProfileCompletion &&
      requiresProfileCompletion &&
      !allowIncompleteProfile &&
      !isProfileComplete
    ) {
      return <Navigate to="/dashboard" replace />;
    }

    if (authorize && !authorize(user)) {
      return <Navigate to="/dashboard" replace />;
    }

    return component;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        isProfileComplete,
        missingProfileFields,
      }}
    >
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            {publicRoutes.map((route) => {
              switch (route.path) {
                case "/":
                  return <Route key={route.path} path={route.path} element={<HomePage />} />;
                case "/statuto":
                  return <Route key={route.path} path={route.path} element={<StatutoPage />} />;
                case "/regolamento":
                  return <Route key={route.path} path={route.path} element={<RegolamentoPage />} />;
                case "/advisory-board":
                  return <Route key={route.path} path={route.path} element={<AdvisoryBoardPage />} />;
                case "/contatti":
                  return <Route key={route.path} path={route.path} element={<ContattiPage />} />;
                case "/login":
                  return <Route key={route.path} path={route.path} element={<LoginPage />} />;
                case "/evento/:token":
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<PublicActivityRegistrationPage />}
                    />
                  );
                case "/evento/:token/conferma":
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<ActivityRegistrationConfirmPage />}
                    />
                  );
                case "/evento/:token/registra":
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<ActivityPostLoginRegistration />}
                    />
                  );
                case "/payment/activity/:activityId":
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<ActivityPaymentPage />}
                    />
                  );
                case "/payment-success":
                  return <Route key={route.path} path={route.path} element={<PaymentSuccess />} />;
                case "/cookie-policy":
                  return <Route key={route.path} path={route.path} element={<CookiePolicyPage />} />;
                case "/privacy-policy":
                  return <Route key={route.path} path={route.path} element={<PrivacyPolicyPage />} />;
                default:
                  return null;
              }
            })}

            {/* Private routes */}
            <Route
              path="/dashboard"
              element={renderProtectedRoute(<Dashboard />, { allowIncompleteProfile: true })}
            />
            <Route
              path="/members"
              element={renderProtectedRoute(<MembersPage />)}
            />
            <Route
              path="/profile"
              element={renderProtectedRoute(<ProfilePage />, { allowIncompleteProfile: true })}
            />
            <Route
              path="/jobs"
              element={renderProtectedRoute(<JobsPage />)}
            />
            <Route
              path="/activities"
              element={renderProtectedRoute(<ActivitiesPage />)}
            />
            <Route
              path="/news"
              element={renderProtectedRoute(<NewsPage />)}
            />
            <Route
              path="/payments"
              element={renderProtectedRoute(<PaymentsPage />)}
            />

            {/* Admin routes */}
            <Route
              path="/admin/roles"
              element={renderProtectedRoute(<RoleManagementPage />)}
            />
            <Route
              path="/admin/payments"
              element={renderProtectedRoute(<StripePaymentsPage />)}
            />
            <Route
              path="/admin/voting-results"
              element={renderProtectedRoute(<VotingResultsPage />)}
            />
            <Route
              path="/admin/quote-situation"
              element={renderProtectedRoute(<QuoteSituationPage />)}
            />
            <Route
              path="/admin/recent-accesses"
              element={renderProtectedRoute(<AdminRecentAccessesPage />)}
            />
            <Route
              path="/admin/applications"
              element={renderProtectedRoute(<AdminApplicationsPage />, {
                authorize: (currentUser) => currentUser.role === "admin",
              })}
            />
            <Route
              path="/admin/votes"
              element={renderProtectedRoute(<AdminVotesPage />, {
                authorize: (currentUser) => currentUser.role === "admin",
              })}
            />
            <Route
              path="/admin/groups"
              element={renderProtectedRoute(<GroupsManagementPage />, {
                authorize: (currentUser) => currentUser.role === "super_admin",
              })}
            />
            <Route
              path="/member-news"
              element={renderProtectedRoute(<MemberNewsPage />)}
            />
            <Route
              path="/payment-intents"
              element={renderProtectedRoute(<PaymentIntentsPage />, {
                authorize: (currentUser) => currentUser.role === "super_admin" || currentUser.role === "admin",
              })}
            />
            <Route
              path="/vote/:candidateId"
              element={renderProtectedRoute(<VotePage />)}
            />
            <Route
              path="/configurations"
              element={renderProtectedRoute(<ConfigurationsPage />)}
            />
            <Route
              path="/archive"
              element={renderProtectedRoute(<ArchivePage />)}
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
